import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';

import { Button, OverlayTrigger, Popover, Form } from "react-bootstrap";
import CreatableSelect from 'react-select/creatable';
import { Fonts, Widgets } from '@paperflite-marketing/cleverstory-commons';

import { useChildrenWidgetSelected, useWidgetChildrenSelector, useGroupContainerSelector } from 'components/Editor/hooks/useEditorSession';
import { Property } from '../../components/properties';
import { LiveElementCreationProperty } from 'components/Editor/components/liveElements';
import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import { EditorContext } from '../../index';

import { toggleEditMode, updateWidgetProperty, switchStepInForm } from 'actions/editor';
import { FORM_STEP } from 'constants/widgetTypes';

import styles from './style.module.scss';
import userRoleService from 'services/userRoles.service';

const ExternalFormWidget = React.memo(function ExternalFormEditor({ className, style, widgetProps, widgetRef, children }) {
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const [state, setState] = useState({
    formStyles: {}
  });

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  Fonts.useFont(_.get(widgetProps, 'fonts') || []);

  var formSteps = children.filter(item => _.get(item, 'props.widgetProps.type') === FORM_STEP)
    .map(item => _.get(item, 'props.widgetProps'));

  formSteps.push(widgetProps)

  formSteps = _.orderBy(formSteps, ['order'], ['asc']);
  const formStep = widgetProps;

  const step = _.get(widgetProps, 'step', _.get(formStep, 'uid', null));

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const properties = _.get(widgetProps, 'properties', {});
  const deviceProperties = _.get(properties, `${breakpoint}`, {});

  const childrenSelection = useChildrenWidgetSelected(editor, id);

  Fonts.useFont(_.get(widgetProps, 'fonts') || []);

  useEffect(() => {
    var formStyles = getStyles(properties, breakpoint);
    setState(state => {
      return {
        ...state,
        formStyles: formStyles
      }
    })
  }, [deviceProperties]);

  var widgetStyle = {
    ...style,
    ...state.formStyles
  }

  return (
    <div id={id}
      ref={ref}
      className={cx(className, styles.form, {
        [styles.partialSelection]: childrenSelection.selected
      })}
      style={widgetStyle}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    >

      {React.Children.map(children,
        child => {
          const active = _.get(child, 'props.widgetProps.uid') === step;

          if (!active) {
            return (
              <div className={styles.externalFormContainer}>
                <Widgets.Components.ExternalFormView widgetProps={widgetProps} breakpoint={breakpoint} />
              </div>
            )
          }
          return (
            React.cloneElement(child, {
              activeStep: active
            }, null)
          );
        })
      }
    </div>
  )
});

const ExternalFormStepDropdown = ({ steps, selected, onChange }) => {
  const [show, setShow] = useState(false);
  const activeStep = steps.find(item => item.uid === selected);

  const handleChange = (step) => {
    onChange(step.uid);
    setShow(false);
  }

  const popover = (
    <Popover className={styles.stepsDropdown}>
      <Popover.Body>
        {steps.map(step => {
          const active = step.uid === selected;
          return (
            <button
              key={step.uid}
              className={cx({ [styles.active]: active })}
              onClick={() => handleChange(step)}
            >{step.name}</button>
          );
        })}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      rootClose
      show={show}
      placement="bottom"
      overlay={popover}
      onHide={() => setShow(false)}
    >
      <Button className={styles.steps} onClick={() => setShow(!show)}>State - {_.get(activeStep, 'name')}</Button>
    </OverlayTrigger>
  );
}

const ExternalFormWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const isGroupContainer = useGroupContainerSelector(editorSession, props);

  const id = _.get(props, 'uid');

  var successStep = useWidgetChildrenSelector(editorSession, id)[0];

  var steps = [];

  var formRequest = {
    name: props.name,
    order: 1,
    parent: props.parent,
    properties: props.properties,
    type: props.type,
    uid: props.uid
  }

  var successRequest = {
    ...successStep,
    order: 2
  }

  steps.push(formRequest);
  steps.push(successRequest);

  steps = _.orderBy(steps, 'order', 'asc');
  const activeStep = _.get(props, 'step') || _.get(steps, '0.uid');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const handleStepChange = (step) => {
    dispatch(switchStepInForm(editorSession, id, step));
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <ExternalFormStepDropdown steps={steps} selected={activeStep} onChange={handleStepChange} />
      <Property.Divider />
      <Property.Action tooltip='Style' onClick={() => changeView('style')}>
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      <Property.Action tooltip='Notification' onClick={() => changeView('email')}>
        <i className={cx(styles.mailIcon, "cleverstory-mail-notification")} />
      </Property.Action>

      {!isGroupContainer &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => changeView('liveElements')}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }


      {!editMode && view === 'style' &&
        <FormStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }
      {!editMode && view === 'brandMedia' &&
        <BrandComponentProperty type={'FORMS'} widgets={[props]} onCancel={() => closePopup(null)} />
      }

      {!editMode && view === 'email' &&
        <FormEmailTemplateEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const ExternalFormWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const isGroupContainer = useGroupContainerSelector(editorSession, props);

  const id = _.get(props, 'uid');

  var successStep = useWidgetChildrenSelector(editorSession, id)[0];

  var steps = [];

  var formRequest = {
    name: props.name,
    order: 1,
    parent: props.parent,
    properties: props.properties,
    type: props.type,
    uid: props.uid
  }

  var successRequest = {
    ...successStep,
    order: 2
  }

  steps.push(formRequest);
  steps.push(successRequest);

  steps = _.orderBy(steps, 'order', 'asc');
  const activeStep = _.get(props, 'step') || _.get(steps, '0.uid');

  const handleStepChange = (step) => {
    dispatch(switchStepInForm(editorSession, id, step));
  }

  return (
    <>
      <ExternalFormStepDropdown steps={steps} selected={activeStep} onChange={handleStepChange} />

      {!isGroupContainer &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => setView('liveElements')}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => setView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => setView(null)} />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'FORMS'} widgets={[props]} onCancel={() => setView(null)} />
      }
    </>
  );
}

ExternalFormWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    renderDirections: ['e', 'w', 's']
  }
}

ExternalFormWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <ExternalFormWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }

      {breakpoint === 'mobile' &&
        <ExternalFormWidgetMobileAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

const FormStyleEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const properties = _.get(widget, `properties.${breakpoint}`, {});

  const backgroundColor = _.get(properties, 'background', null);

  const border = _.get(properties, 'border', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });


  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      background: color
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      border: {
        ...border,
        color: color
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      border: {
        ...border,
        width: value
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      border: {
        ...border,
        radius: radius
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Customize Form'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property name="Form Design" className={styles.DesignProperty}>
          <Property.Accordion title={'Border'} className={styles.borderProperty}>
            <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
            <Property name="Stroke Width" className={styles.buttonStyleProperty}>
              <Property.Slider
                min={0}
                max={10}
                step={1}
                input
                value={borderWidth}
                onChange={handleBorderWidthChange}
              />
            </Property>
            <Property name="Corner Radius" className={styles.formStyleProperty}>
              <Property.BorderRadius
                value={borderRadius}
                onChange={handleBorderRadiusChange}
              />
            </Property>
          </Property.Accordion>
          <Property.Accordion title={'Background'} className={styles.borderProperty}>
            <Property.Color title={'Color'} value={backgroundColor} onChange={handleBackgroundColorChange} />
          </Property.Accordion>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const FormEmailTemplateEditor = ({ id, editor, widget, position, onCancel, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const properties = _.get(widget, `properties.${breakpoint}`, {});
  var emailTemplate = {
    emails: [],
    subject: "",
    body: "",
    error: null
  };

  const formEmailTemplate = _.get(properties, 'formEmailTemplate', emailTemplate) || emailTemplate;

  const [state, setState] = useState(formEmailTemplate);

  const emails = (_.get(state, 'emails') || []).map(item => {
    return {
      label: item,
      value: item
    }
  });

  const handleSaveEmailTemplate = (e) => {
    e.preventDefault();

    var request = {
      ...properties,
      formEmailTemplate: state
    }

    dispatch(updateWidgetProperty(editorSession, id, breakpoint, request));
    onCancel();
  }

  const handleFieldChange = (field, e) => {
    setState((state) => {
      return {
        ...state,
        [field]: e.target.value,
        error: null,
      };
    });
  }

  const isValid = (email) => {
    let error = null;

    if (isInList(email)) {
      error = `"${email}" has already been added.`;
    }

    if (!isEmail(email)) {
      error = `"${email}" is not a valid email address.`;
    }

    if (isLimitExceeded()) {
      error = `only 5 emails are allowed`;
    }

    if (error) {
      setState(state => {
        return {
          ...state,
          error
        }
      })

      return false;
    }

    return true;
  }

  const isInList = (email) => {
    return state.emails && state.emails.includes(email);
  }

  const isLimitExceeded = () => {
    if (state.emails && state.emails.length >= 5) {
      return true;
    }

    return false;
  }

  const isEmail = (email) => {
    return /[\w\d\.-]+@[\w\d\.-]+\.[\w\d\.-]+/.test(email);
  }

  const handleEmailChange = (data) => {
    const emails = data.map(item => item.value);
    const currentValue = data[data.length - 1];

    if (data.length < state.emails.length) {
      const emails = data.map(item => item.value);
      setState((state) => {
        return {
          ...state,
          emails: emails,
          error: null
        };
      });
    }

    if (currentValue && currentValue.__isNew__) {
      if (isValid(currentValue.value)) {
        setState((state) => {
          return {
            ...state,
            emails: emails,
            error: null
          };
        });
      }
    }

    return;
  }

  return (
    <Property.Dialog
      width={604}
      target={`#${id}`}
      title={'Form submission - Email Notification Setting'}
      onCancel={onCancel}
    >
      <Property.Body className={styles.targetProperty}>
        <Form className={styles.emailFields}>
          <Form.Group className="mb-3" controlId="formEmailToAddress">
            <Form.Label>To:</Form.Label>
            <CreatableSelect
              className={styles.select}
              classNamePrefix="select"
              name="emails"
              placeholder="You can give upto 5 email-id’s"
              isMulti
              options={[]}
              value={emails}
              onChange={handleEmailChange}
            />
            <span className={styles.error}>{state.error}</span>
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>Subject</Form.Label>
            <Form.Control
              onChange={handleFieldChange.bind(this, "subject")}
              type="text"
              placeholder="Ex: We got a lead!"
              value={state.subject} />
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlTextarea1">
            <Form.Label>Body</Form.Label>
            <Form.Control
              onChange={handleFieldChange.bind(this, "body")}
              as="textarea"
              rows={4}
              placeholder="Ex: Someone just submitted the form on your page."
              value={state.body} />
          </Form.Group>
          <div className={styles.buttonTray}>
            <Button
              className={styles.save}
              onClick={handleSaveEmailTemplate}>
              Save
            </Button>
          </div>

        </Form>
      </Property.Body>
    </Property.Dialog>
  )
}

function getStyles(properties, breakpoint) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);

  const font = _.get(defaultProperties, 'font', {});

  //font-color
  const colorRgb = _.get(font, 'color', null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(font, 'fontFamily', 'inherit');
  styles = {
    ...styles,
    fontFamily: fontFamily
  }

  //font-size
  const fontSizeString = _.get(deviceProperties, 'regular.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;


  //padding
  const paddingString = _.get(deviceProperties, 'padding', null)
  var padding = '20'
  if (paddingString) {
    padding = parseInt(paddingString) + 'px';
  }

  styles['padding'] = padding;


  //font-style
  const bold = _.get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = _.get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = _.get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //background
  const backgroundRgb = _.get(defaultProperties, 'background', null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = `rgba(${backgroundRgb.r}, ${backgroundRgb.g}, ${backgroundRgb.b}, ${backgroundRgb.a})`;
  }

  styles['backgroundColor'] = background;

  //border
  const border = _.get(defaultProperties, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //alignment
  const alignment = _.get(deviceProperties, 'alignment', {});

  var justifyContent = _.get(alignment, 'justifyContent', 'left');
  styles['justifyContent'] = justifyContent;

  var flexDirection = _.get(alignment, 'flexDrection', 'column');
  styles['flexDirection'] = flexDirection;

  var alignItems = _.get(alignment, 'alignItems', 'start');
  styles['alignItems'] = alignItems;

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var borderStyles = {}

  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  if (topLeft) {
    borderStyles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    borderStyles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    borderStyles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    borderStyles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  borderStyles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', null);
  var borderWidget = parseInt(borderWidthString) + 'px';

  borderStyles['borderWidth'] = borderWidget;

  return borderStyles;
}

ExternalFormWidget.processRequest = (widget) => {
  return widget;
}

export { ExternalFormWidget };